import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileWarning, Plus, AlertTriangle, CheckCircle, Clock, Eye, Edit, 
  Trash2, Send, User, Calendar, Briefcase, MessageSquare, X,
  FileText, AlertOctagon, AlertCircle as AlertInfo, ThumbsUp, XOctagon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { evaluationsAPI, workersAPI, bookingsAPI } from '../../services/api';

const EVALUATION_TYPES = [
  { value: 'write_up', label: 'Write-Up', icon: FileText, color: 'bg-[var(--error)]/15 text-red-700', description: 'Formal documentation of policy violation or performance issue' },
  { value: 'verbal_warning', label: 'Verbal Warning', icon: AlertInfo, color: 'bg-amber-100 text-amber-700', description: 'Informal verbal notice for minor issues' },
  { value: 'written_warning', label: 'Written Warning', icon: AlertTriangle, color: 'bg-orange-100 text-orange-700', description: 'Formal written notice for repeated or serious issues' },
  { value: 'performance_review', label: 'Performance Review', icon: ThumbsUp, color: 'bg-[var(--success)]/15 text-green-700', description: 'Positive feedback or performance evaluation' },
  { value: 'termination_notice', label: 'Termination Notice', icon: XOctagon, color: 'bg-red-200 text-[var(--error)]', description: 'Formal notice of employment termination' },
  { value: 'custom', label: 'Custom', icon: FileWarning, color: 'bg-[#F4C2C2]/15 text-purple-700', description: 'Custom evaluation type' },
];

const EvaluationsTab = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const [selectedEval, setSelectedEval] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  const [form, setForm] = useState({
    worker_id: '',
    evaluation_type: 'write_up',
    custom_type_name: '',
    title: '',
    description: '',
    booking_id: '',
    incident_date: '',
    requires_improvement: false,
    improvement_plan: '',
    follow_up_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [evalsRes, statsRes, workersRes, bookingsRes] = await Promise.all([
        evaluationsAPI.getAll(),
        evaluationsAPI.getStats(),
        workersAPI.getAll(),
        bookingsAPI.getAll()
      ]);
      setEvaluations(evalsRes.data || []);
      setStats(statsRes.data);
      setWorkers(workersRes.data || []);
      setBookings(bookingsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.worker_id || !form.title || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (form.evaluation_type === 'custom' && !form.custom_type_name) {
      toast.error('Please specify custom type name');
      return;
    }

    try {
      const payload = {
        ...form,
        booking_id: form.booking_id || null,
        incident_date: form.incident_date || null,
        follow_up_date: form.follow_up_date || null,
        improvement_plan: form.improvement_plan || null
      };

      if (editingEval) {
        await evaluationsAPI.update(editingEval.id, payload);
        toast.success('Evaluation updated');
      } else {
        await evaluationsAPI.create(payload);
        toast.success('Evaluation created and worker notified');
      }
      setShowModal(false);
      setEditingEval(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving evaluation:', error);
      toast.error(error.response?.data?.detail || 'Failed to save evaluation');
    }
  };

  const handleDelete = async (evalId) => {
    if (!window.confirm('Are you sure you want to delete this evaluation?')) return;
    
    try {
      await evaluationsAPI.delete(evalId);
      toast.success('Evaluation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete evaluation');
    }
  };

  const handleSendReminder = async (evalId) => {
    try {
      await evaluationsAPI.sendReminder(evalId);
      toast.success('Reminder sent to worker');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const resetForm = () => {
    setForm({
      worker_id: '',
      evaluation_type: 'write_up',
      custom_type_name: '',
      title: '',
      description: '',
      booking_id: '',
      incident_date: '',
      requires_improvement: false,
      improvement_plan: '',
      follow_up_date: ''
    });
  };

  const openEditModal = (evaluation) => {
    setEditingEval(evaluation);
    setForm({
      worker_id: evaluation.worker_id,
      evaluation_type: evaluation.evaluation_type,
      custom_type_name: evaluation.custom_type_name || '',
      title: evaluation.title,
      description: evaluation.description,
      booking_id: evaluation.booking_id || '',
      incident_date: evaluation.incident_date || '',
      requires_improvement: evaluation.requires_improvement,
      improvement_plan: evaluation.improvement_plan || '',
      follow_up_date: evaluation.follow_up_date || ''
    });
    setShowModal(true);
  };

  const getTypeConfig = (type) => {
    return EVALUATION_TYPES.find(t => t.value === type) || EVALUATION_TYPES[5];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge-warning flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case 'viewed':
        return <span className="badge-primary flex items-center gap-1"><Eye className="w-3 h-3" /> Viewed</span>;
      case 'signed':
        return <span className="badge-success flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Signed</span>;
      default:
        return <span className="badge-secondary">{status}</span>;
    }
  };

  // Filter evaluations
  const filteredEvaluations = evaluations.filter(e => {
    if (filterStatus !== 'all' && e.status !== filterStatus) return false;
    if (filterType !== 'all' && e.evaluation_type !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Evaluations & Discipline</h1>
          <p className="text-[var(--text-muted)] mt-1">Manage write-ups, warnings, and performance reviews</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingEval(null); setShowModal(true); }} 
          className="btn-primary" 
          data-testid="create-evaluation-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Evaluation
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--primary)]">{stats.total}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Evaluations</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending + stats.viewed}</p>
            <p className="text-sm text-[var(--text-muted)]">Awaiting Signature</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{stats.signed}</p>
            <p className="text-sm text-[var(--text-muted)]">Signed</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--error)]">{stats.by_type?.write_up || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Write-Ups</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Status:</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="viewed">Viewed</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Type:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {EVALUATION_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Evaluations List */}
      {filteredEvaluations.length === 0 ? (
        <div className="card p-12 text-center">
          <FileWarning className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Evaluations Found</h3>
          <p className="text-[var(--text-muted)] mb-4">
            {evaluations.length === 0 
              ? 'Create your first evaluation to document worker performance or disciplinary actions.'
              : 'No evaluations match the current filters.'}
          </p>
          <Button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create First Evaluation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEvaluations.map(evaluation => {
            const typeConfig = getTypeConfig(evaluation.evaluation_type);
            const TypeIcon = typeConfig.icon;
            
            return (
              <div 
                key={evaluation.id} 
                className="card p-6"
                data-testid={`evaluation-${evaluation.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${typeConfig.color}`}>
                        <TypeIcon className="w-4 h-4" />
                        {evaluation.custom_type_name || typeConfig.label}
                      </span>
                      {getStatusBadge(evaluation.status)}
                    </div>
                    
                    <h3 className="text-lg font-bold mb-2">{evaluation.title}</h3>
                    
                    <div className="flex items-center gap-4 text-sm text-[var(--text-muted)] mb-3">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {evaluation.worker_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(evaluation.created_at).toLocaleDateString()}
                      </span>
                      {evaluation.booking_info && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {evaluation.booking_info.service_name}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-3">
                      {evaluation.description}
                    </p>
                    
                    {evaluation.worker_response && (
                      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-blue-800 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          Worker Response:
                        </p>
                        <p className="text-sm text-[#D4AF37]">{evaluation.worker_response}</p>
                      </div>
                    )}
                    
                    {evaluation.signed_at && (
                      <p className="text-xs text-[var(--success)]">
                        Signed: {new Date(evaluation.signed_at).toLocaleString()}
                      </p>
                    )}
                    
                    {evaluation.reminder_sent_at && evaluation.status !== 'signed' && (
                      <p className="text-xs text-amber-600">
                        Reminder sent: {new Date(evaluation.reminder_sent_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedEval(evaluation)}
                      data-testid={`view-evaluation-${evaluation.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    
                    {evaluation.status !== 'signed' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendReminder(evaluation.id)}
                          className="text-amber-600 border-amber-300 hover:bg-[var(--warning)]/10"
                          data-testid={`remind-evaluation-${evaluation.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Remind
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(evaluation)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(evaluation.id)}
                      className="text-[var(--error)] hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-[var(--border-primary)] flex justify-between items-center">
              <h2 className="text-xl font-bold font-['Cormorant_Garamond']">
                {editingEval ? 'Edit Evaluation' : 'Create New Evaluation'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => { setShowModal(false); setEditingEval(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Worker Selection */}
              <div>
                <Label>Worker *</Label>
                <Select 
                  value={form.worker_id} 
                  onValueChange={(value) => setForm({ ...form, worker_id: value })}
                  disabled={!!editingEval}
                >
                  <SelectTrigger className="input-styled mt-1">
                    <SelectValue placeholder="Select worker..." />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.filter(w => w.is_active).map(worker => (
                      <SelectItem key={worker.id} value={worker.id}>
                        {worker.full_name} ({worker.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Evaluation Type */}
              <div>
                <Label>Evaluation Type *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {EVALUATION_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setForm({ ...form, evaluation_type: type.value })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          form.evaluation_type === type.value 
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5' 
                            : 'border-[var(--border-primary)] hover:border-[var(--border-primary)]'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-4 h-4 ${type.color.split(' ')[1]}`} />
                          <span className="font-medium text-sm">{type.label}</span>
                        </div>
                        <p className="text-xs text-[var(--text-muted)] line-clamp-2">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Type Name */}
              {form.evaluation_type === 'custom' && (
                <div>
                  <Label>Custom Type Name *</Label>
                  <Input
                    value={form.custom_type_name}
                    onChange={(e) => setForm({ ...form, custom_type_name: e.target.value })}
                    placeholder="e.g., Safety Violation"
                    className="input-styled mt-1"
                  />
                </div>
              )}

              {/* Title */}
              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Late Arrival - March 15th"
                  className="input-styled mt-1"
                  data-testid="evaluation-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Detailed description of the incident, behavior, or performance issue..."
                  className="input-styled mt-1 min-h-[150px]"
                  data-testid="evaluation-description-input"
                />
              </div>

              {/* Link to Booking (Optional) */}
              <div>
                <Label>Link to Booking (Optional)</Label>
                <Select 
                  value={form.booking_id || "none"} 
                  onValueChange={(value) => setForm({ ...form, booking_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="input-styled mt-1">
                    <SelectValue placeholder="Select booking..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked booking</SelectItem>
                    {bookings.slice(0, 50).map(booking => (
                      <SelectItem key={booking.id} value={booking.id}>
                        {booking.service_name} - {booking.scheduled_date} ({booking.customer_name || booking.customer_email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Incident Date */}
              <div>
                <Label>Incident Date (Optional)</Label>
                <Input
                  type="date"
                  value={form.incident_date}
                  onChange={(e) => setForm({ ...form, incident_date: e.target.value })}
                  className="input-styled mt-1"
                />
              </div>

              {/* Requires Improvement */}
              <div className="flex items-start gap-3 p-4 bg-[var(--warning)]/10 rounded-lg">
                <Checkbox
                  checked={form.requires_improvement}
                  onCheckedChange={(checked) => setForm({ ...form, requires_improvement: checked })}
                />
                <div>
                  <Label className="font-medium">Requires Improvement Plan</Label>
                  <p className="text-xs text-[var(--text-muted)]">
                    Worker must follow an improvement plan
                  </p>
                </div>
              </div>

              {/* Improvement Plan */}
              {form.requires_improvement && (
                <>
                  <div>
                    <Label>Improvement Plan</Label>
                    <Textarea
                      value={form.improvement_plan}
                      onChange={(e) => setForm({ ...form, improvement_plan: e.target.value })}
                      placeholder="Outline the steps the worker must take to improve..."
                      className="input-styled mt-1 min-h-[100px]"
                    />
                  </div>
                  <div>
                    <Label>Follow-up Date</Label>
                    <Input
                      type="date"
                      value={form.follow_up_date}
                      onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                      className="input-styled mt-1"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="p-6 border-t border-[var(--border-primary)] flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowModal(false); setEditingEval(null); }}>
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={handleSubmit}
                data-testid="save-evaluation-btn"
              >
                {editingEval ? 'Update Evaluation' : 'Create & Notify Worker'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* View Details Modal */}
      {selectedEval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-[var(--border-primary)] flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold font-['Cormorant_Garamond']">{selectedEval.title}</h2>
                <p className="text-sm text-[var(--text-muted)]">
                  {getTypeConfig(selectedEval.evaluation_type).label} for {selectedEval.worker_name}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedEval(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                {getStatusBadge(selectedEval.status)}
                <span className={`px-3 py-1 rounded-full text-sm ${getTypeConfig(selectedEval.evaluation_type).color}`}>
                  {selectedEval.custom_type_name || getTypeConfig(selectedEval.evaluation_type).label}
                </span>
              </div>
              
              <div className="prose prose-sm max-w-none">
                <h4>Description</h4>
                <p className="whitespace-pre-wrap">{selectedEval.description}</p>
              </div>
              
              {selectedEval.incident_date && (
                <div>
                  <strong>Incident Date:</strong> {selectedEval.incident_date}
                </div>
              )}
              
              {selectedEval.booking_info && (
                <div className="bg-[var(--background-subtle)] p-4 rounded-lg">
                  <strong>Linked Booking:</strong>
                  <p>{selectedEval.booking_info.service_name} on {selectedEval.booking_info.scheduled_date}</p>
                  <p className="text-sm text-[var(--text-muted)]">Customer: {selectedEval.booking_info.customer_name}</p>
                </div>
              )}
              
              {selectedEval.requires_improvement && selectedEval.improvement_plan && (
                <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 p-4 rounded-lg">
                  <strong className="text-amber-800">Improvement Plan:</strong>
                  <p className="mt-2 whitespace-pre-wrap">{selectedEval.improvement_plan}</p>
                  {selectedEval.follow_up_date && (
                    <p className="mt-2 text-sm text-amber-700">Follow-up: {selectedEval.follow_up_date}</p>
                  )}
                </div>
              )}
              
              {selectedEval.worker_response && (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 rounded-lg">
                  <strong className="text-blue-800">Worker Response:</strong>
                  <p className="mt-2 whitespace-pre-wrap">{selectedEval.worker_response}</p>
                </div>
              )}
              
              <div className="text-sm text-[var(--text-muted)] space-y-1">
                <p>Created by: {selectedEval.created_by_name} on {new Date(selectedEval.created_at).toLocaleString()}</p>
                {selectedEval.viewed_at && <p>Viewed: {new Date(selectedEval.viewed_at).toLocaleString()}</p>}
                {selectedEval.signed_at && <p>Signed: {new Date(selectedEval.signed_at).toLocaleString()}</p>}
              </div>
            </div>
            
            <div className="p-6 border-t border-[var(--border-primary)] flex justify-end gap-3">
              {selectedEval.status !== 'signed' && (
                <Button 
                  variant="outline"
                  onClick={() => { handleSendReminder(selectedEval.id); }}
                  className="text-amber-600"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminder
                </Button>
              )}
              <Button onClick={() => setSelectedEval(null)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default EvaluationsTab;
